import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function ConsultationEtudiant() {
    const [codePin, setCodePin] = useState('');
    const [etudiant, setEtudiant] = useState(null);
    const [loading, setLoading] = useState(false);
    const [produits, setProduits] = useState([]);
    const [searchProduit, setSearchProduit] = useState('');
    const [selectedProduits, setSelectedProduits] = useState([]);
    const [motif, setMotif] = useState('');
    const [diagnostic, setDiagnostic] = useState('');
    const [traitement, setTraitement] = useState('');
    const [showProduitModal, setShowProduitModal] = useState(false);
    const [quantiteTemp, setQuantiteTemp] = useState(1);
    const [produitTemp, setProduitTemp] = useState(null);
    const [sending, setSending] = useState(false);

    // Utilisation de la configuration centralisée
    const API_BASE = config.apiBase;

    const rechercherEtudiant = async () => {
        if (!codePin || codePin.length < 6) {
            toast.error('Code PIN invalide (6 chiffres requis)');
            return;
        }
        
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/infirmier/rechercher_etudiant.php?code_pin=${codePin}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            
            if (data.success && data.etudiant) {
                setEtudiant(data.etudiant);
                toast.success(`Étudiant trouvé: ${data.etudiant.prenom} ${data.etudiant.nom}`);
            } else {
                toast.error(data.message || 'Étudiant non trouvé');
                setEtudiant(null);
            }
        } catch (error) {
            toast.error('Erreur de recherche');
        } finally {
            setLoading(false);
        }
    };

    const chargerProduits = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/infirmier/get_produits.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setProduits(data.produits);
            }
        } catch (error) {
            toast.error('Erreur chargement produits');
        }
    };

    useEffect(() => {
        chargerProduits();
    }, []);

    const produitsFiltres = produits.filter(p =>
        p.nom.toLowerCase().includes(searchProduit.toLowerCase())
    );

    const ajouterProduit = (produit, quantite) => {
        if (quantite <= 0) {
            toast.error('Quantité invalide');
            return;
        }
        if (quantite > produit.quantite) {
            toast.error(`Stock insuffisant (${produit.quantite} restants)`);
            return;
        }
        
        const existant = selectedProduits.find(p => p.id_produit === produit.id_produit);
        if (existant) {
            const nouvelleQuantite = existant.quantite + quantite;
            if (nouvelleQuantite > produit.quantite) {
                toast.error(`Stock insuffisant (${produit.quantite} restants)`);
                return;
            }
            setSelectedProduits(selectedProduits.map(p =>
                p.id_produit === produit.id_produit ? { ...p, quantite: nouvelleQuantite } : p
            ));
        } else {
            setSelectedProduits([...selectedProduits, { ...produit, quantite }]);
        }
        setShowProduitModal(false);
        setSearchProduit('');
        setQuantiteTemp(1);
        toast.success(`${produit.nom} ajouté (${quantite} ${produit.unite})`);
    };

    const supprimerProduit = (id) => {
        setSelectedProduits(selectedProduits.filter(p => p.id_produit !== id));
    };

    const debiterProduits = async () => {
        if (selectedProduits.length === 0) {
            toast.error('Aucun produit sélectionné');
            return;
        }
        
        setSending(true);
        
        let successCount = 0;
        for (const produit of selectedProduits) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE}/infirmier/debiter_produit.php`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        id_produit: produit.id_produit,
                        quantite: produit.quantite,
                        id_etudiant: etudiant.id_utilisateur,
                        code_pin: codePin,
                        motif: motif
                    })
                });
                const data = await response.json();
                if (data.success) {
                    successCount++;
                }
            } catch (error) {
                console.error('Erreur:', error);
            }
        }
        
        if (successCount > 0) {
            toast.success(`${successCount} produit(s) débité(s)`);
            chargerProduits();
            setSelectedProduits([]);
        } else {
            toast.error('Erreur lors du débit');
        }
        setSending(false);
    };

    const envoyerRapport = async () => {
        if (!diagnostic.trim()) {
            toast.error('Veuillez renseigner le diagnostic');
            return;
        }
        
        setSending(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/infirmier/envoyer_rapport.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id_etudiant: etudiant.id_utilisateur,
                    diagnostic: diagnostic,
                    traitement: traitement,
                    produits: selectedProduits
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Rapport envoyé au financier');
                setDiagnostic('');
                setTraitement('');
                setMotif('');
                setSelectedProduits([]);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur lors de l\'envoi');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="mx-auto max-w-6xl">
                {/* Header */}
                <div className="mb-8 rounded-2xl bg-gradient-to-r from-rose-600 to-pink-600 p-6 text-white shadow-xl">
                    <h1 className="text-2xl font-bold">🏥 Consultation médicale</h1>
                    <p className="mt-2 opacity-90">
                        Recherchez un étudiant par code PIN pour les soins
                    </p>
                </div>

                {/* Recherche par code PIN */}
                <div className="mb-6 rounded-2xl bg-white p-6 shadow-xl">
                    <div className="flex gap-3">
                        <input
                            type="password"
                            placeholder="🔑 Code PIN (6 chiffres)"
                            value={codePin}
                            onChange={(e) => setCodePin(e.target.value)}
                            className="flex-1 rounded-xl border border-slate-200 px-4 py-3 text-center text-2xl tracking-widest outline-none focus:border-rose-400"
                            maxLength="6"
                            onKeyPress={(e) => e.key === 'Enter' && rechercherEtudiant()}
                        />
                        <button
                            onClick={rechercherEtudiant}
                            disabled={loading}
                            className="rounded-xl bg-rose-600 px-6 py-3 text-white transition hover:bg-rose-700 disabled:opacity-50"
                        >
                            {loading ? 'Recherche...' : '🔍 Rechercher'}
                        </button>
                    </div>
                </div>

                {/* Informations étudiant */}
                {etudiant && (
                    <div className="mb-6 rounded-2xl bg-white p-6 shadow-xl">
                        <div className="flex flex-col md:flex-row gap-6">
                            <div className="flex-shrink-0 text-center">
                                {etudiant.photo_profil ? (
                                    <img src={config.normalizeProfilePhoto(etudiant.photo_profil)} alt="Profil" className="w-32 h-32 rounded-full object-cover border-4 border-rose-500 mx-auto" />
                                ) : (
                                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center text-4xl text-white mx-auto">
                                        {etudiant.prenom?.charAt(0)}{etudiant.nom?.charAt(0)}
                                    </div>
                                )}
                            </div>
                            <div className="flex-1">
                                <div className="mb-2">
                                    <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-700">
                                        ✅ Étudiant confirmé
                                    </span>
                                </div>
                                <h2 className="text-2xl font-bold text-slate-800">{etudiant.prenom} {etudiant.nom}</h2>
                                <p className="text-slate-500">{etudiant.email}</p>
                                <p className="text-slate-500">{etudiant.telephone}</p>
                            </div>
                            {etudiant.carte_identite && (
                                <div className="flex-shrink-0">
                                    <p className="text-sm font-semibold text-slate-600 mb-2">📄 Carte d'identité</p>
                                    <img src={config.normalizeDocument(etudiant.carte_identite)} alt="Carte identité" className="max-h-40 rounded-lg border" />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Formulaire de traitement */}
                {etudiant && (
                    <div className="rounded-2xl bg-white p-6 shadow-xl">
                        <h2 className="text-xl font-bold text-slate-800 mb-4">📋 Traitement médical</h2>
                        
                        {/* Diagnostic */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Diagnostic *</label>
                            <textarea
                                value={diagnostic}
                                onChange={(e) => setDiagnostic(e.target.value)}
                                className="w-full rounded-xl border border-slate-200 px-4 py-2 outline-none focus:border-rose-400"
                                rows="2"
                                placeholder="Symptômes, diagnostic..."
                            />
                        </div>
                        
                        {/* Traitement prescrit */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Traitement prescrit</label>
                            <textarea
                                value={traitement}
                                onChange={(e) => setTraitement(e.target.value)}
                                className="w-full rounded-xl border border-slate-200 px-4 py-2 outline-none focus:border-rose-400"
                                rows="2"
                                placeholder="Posologie, recommandations..."
                            />
                        </div>
                        
                        {/* Motif des soins */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Motif des soins</label>
                            <textarea
                                value={motif}
                                onChange={(e) => setMotif(e.target.value)}
                                className="w-full rounded-xl border border-slate-200 px-4 py-2 outline-none focus:border-rose-400"
                                rows="2"
                                placeholder="Raison de la consultation..."
                            />
                        </div>
                        
                        {/* Moteur de recherche produits */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-slate-700 mb-1">🔍 Produits utilisés</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Rechercher un produit..."
                                    value={searchProduit}
                                    onChange={(e) => setSearchProduit(e.target.value)}
                                    className="flex-1 rounded-xl border border-slate-200 px-4 py-2 outline-none focus:border-rose-400"
                                />
                                <button
                                    onClick={() => setShowProduitModal(true)}
                                    className="rounded-xl bg-rose-600 px-4 py-2 text-white hover:bg-rose-700"
                                >
                                    + Ajouter
                                </button>
                            </div>
                        </div>
                        
                        {/* Liste des produits sélectionnés */}
                        {selectedProduits.length > 0 && (
                            <div className="mb-4 rounded-xl border border-slate-200 p-4">
                                <h3 className="font-semibold text-slate-800 mb-2">Produits sélectionnés :</h3>
                                <div className="space-y-2">
                                    {selectedProduits.map(p => (
                                        <div key={p.id_produit} className="flex items-center justify-between bg-slate-50 p-2 rounded-lg">
                                            <div>
                                                <span className="font-medium">{p.nom}</span>
                                                <span className="text-sm text-slate-500 ml-2">x{p.quantite} {p.unite}</span>
                                            </div>
                                            <button
                                                onClick={() => supprimerProduit(p.id_produit)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Boutons d'action */}
                        <div className="flex gap-3">
                            <button
                                onClick={debiterProduits}
                                disabled={sending || selectedProduits.length === 0}
                                className="flex-1 bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700 disabled:opacity-50"
                            >
                                💊 Débiter les produits
                            </button>
                            <button
                                onClick={envoyerRapport}
                                disabled={sending || !diagnostic.trim()}
                                className="flex-1 bg-green-600 text-white py-2 rounded-xl hover:bg-green-700 disabled:opacity-50"
                            >
                                📤 Envoyer le rapport
                            </button>
                        </div>
                    </div>
                )}

                {/* Modal sélection produit */}
                {showProduitModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                        <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
                            <div className="border-b p-4">
                                <h3 className="text-xl font-bold text-slate-800">Sélectionner un produit</h3>
                            </div>
                            <div className="p-4">
                                <input
                                    type="text"
                                    placeholder="Rechercher..."
                                    value={searchProduit}
                                    onChange={(e) => setSearchProduit(e.target.value)}
                                    className="w-full rounded-xl border border-slate-200 px-4 py-2 mb-3 outline-none focus:border-rose-400"
                                    autoFocus
                                />
                                <div className="max-h-64 overflow-y-auto space-y-2">
                                    {produitsFiltres.map(p => (
                                        <div
                                            key={p.id_produit}
                                            onClick={() => {
                                                setProduitTemp(p);
                                                setQuantiteTemp(1);
                                            }}
                                            className="cursor-pointer rounded-lg border p-3 hover:bg-slate-50"
                                        >
                                            <p className="font-medium">{p.nom}</p>
                                            <p className="text-sm text-slate-500">Stock: {p.quantite} {p.unite}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="border-t p-4">
                                <div className="flex gap-3">
                                    <input
                                        type="number"
                                        min="1"
                                        value={quantiteTemp}
                                        onChange={(e) => setQuantiteTemp(parseInt(e.target.value) || 1)}
                                        className="w-24 rounded-xl border px-3 py-2 text-center"
                                    />
                                    <button
                                        onClick={() => produitTemp && ajouterProduit(produitTemp, quantiteTemp)}
                                        className="flex-1 bg-rose-600 text-white py-2 rounded-xl hover:bg-rose-700"
                                    >
                                        Ajouter
                                    </button>
                                    <button
                                        onClick={() => setShowProduitModal(false)}
                                        className="bg-slate-200 px-4 py-2 rounded-xl hover:bg-slate-300"
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}